import { useState, useEffect } from "react";
import { rootUrl } from "../../helpers";

const ROWS_PER_PAGE = 10;

const paginate = (data, page, rowsPerPage) => {
  const start = page * rowsPerPage;
  return data.slice(start, start + rowsPerPage);
};

const AuditPage = () => {
  const [auditData, setAuditData] = useState([]);
  const [cashbookData, setCashbookData] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [tab, setTab] = useState("transactions");
  const [selectedUser, setSelectedUser] = useState(null);
  // Pagination states
  const [txPage, setTxPage] = useState(0);
  const [auditPage, setAuditPage] = useState(0);
  const [userPage, setUserPage] = useState(0);
  const [userTxPage, setUserTxPage] = useState(0);
  const [userAuditPage, setUserAuditPage] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      const response = await fetch(rootUrl('/auditReport'));
      const data = await response.json();
      setAuditData(data.data.audit);
      setCashbookData(data.data.cashbook);
      setUsers(data.data.users);
    };
    fetchData();
  }, []);

  const getUserById = (id) => users.find((user) => user.id === id)?.firstname || "Unknown";

  // Filters for Transactions
  const filteredCashbook = cashbookData.filter(
    (entry) =>
      (!startDate || new Date(entry.date) >= new Date(startDate)) &&
      (!endDate || new Date(entry.date) <= new Date(endDate)) &&
      (!search || getUserById(entry.updatedBy).toLowerCase().includes(search.toLowerCase()))
  );

  // Filters for Audit Log
  const filteredAudit = auditData.filter(
    (entry) =>
      (!startDate || new Date(entry.date) >= new Date(startDate)) &&
      (!endDate || new Date(entry.date) <= new Date(endDate)) &&
      (!search || getUserById(entry.uid).toLowerCase().includes(search.toLowerCase()))
  );

  // By User: list of users with counts
  const userActions = users.map((user) => {
    const userTransactions = cashbookData.filter((entry) => entry.updatedBy === user.id);
    const userAudits = auditData.filter((entry) => entry.uid === user.id);
    return {
      ...user,
      transactionCount: userTransactions.length,
      auditCount: userAudits.length,
      transactions: userTransactions,
      audits: userAudits,
    };
  });

  // Transaction totals
  const totalIncome = filteredCashbook.filter(e => e.type === 'income').reduce((sum, e) => sum + Number(e.amount), 0);
  const totalExpense = filteredCashbook.filter(e => e.type === 'expense').reduce((sum, e) => sum + Number(e.amount), 0);
  const net = totalIncome - totalExpense;

  // Paginated data
  const pagedTx = paginate(filteredCashbook, txPage, ROWS_PER_PAGE);
  const pagedAudit = paginate(filteredAudit, auditPage, ROWS_PER_PAGE);
  const pagedUsers = paginate(userActions, userPage, ROWS_PER_PAGE);
  const pagedUserTx = selectedUser ? paginate(selectedUser.transactions, userTxPage, ROWS_PER_PAGE) : [];
  const pagedUserAudit = selectedUser ? paginate(selectedUser.audits, userAuditPage, ROWS_PER_PAGE) : [];

  // Reset page on tab/filter change
  useEffect(() => { setTxPage(0); }, [search, startDate, endDate, tab]);
  useEffect(() => { setAuditPage(0); }, [search, startDate, endDate, tab]);
  useEffect(() => { setUserPage(0); }, [tab]);
  useEffect(() => { setUserTxPage(0); setUserAuditPage(0); }, [selectedUser]);

  return (
    <div className="p-6 bg-gray-100 min-h-screen bg-white shadow-lg rounded-lg">
      <h2 className="text-2xl font-bold mb-4">Audit & Transactions</h2>
      {/* Tabs */}
      <div className="flex gap-4 mb-6">
        <button onClick={() => { setTab('transactions'); setSelectedUser(null); }} className={`px-4 py-2 rounded ${tab === 'transactions' ? 'bg-blue-900 text-white' : 'bg-gray-200'}`}>Transactions</button>
        <button onClick={() => { setTab('audit'); setSelectedUser(null); }} className={`px-4 py-2 rounded ${tab === 'audit' ? 'bg-blue-900 text-white' : 'bg-gray-200'}`}>Audit Log</button>
        <button onClick={() => setTab('users')} className={`px-4 py-2 rounded ${tab === 'users' ? 'bg-blue-900 text-white' : 'bg-gray-200'}`}>By User</button>
      </div>

      {/* Filters */}
      {tab !== 'users' && (
        <div className="flex gap-4 mb-4">
          <input
            type="text"
            placeholder="Search by Username"
            className="p-2 border rounded w-1/3"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <input
            type="date"
            className="p-2 border rounded"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <input
            type="date"
            className="p-2 border rounded"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
      )}

      {/* Transactions Tab */}
      {tab === 'transactions' && (
        <div>
          <h3 className="text-xl font-semibold mb-2">Cash Transactions</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white shadow rounded">
              <thead>
                <tr className="bg-gray-200">
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2">Amount</th>
                  <th className="px-4 py-2">Description</th>
                  <th className="px-4 py-2">Performed By</th>
                </tr>
              </thead>
              <tbody>
                {pagedTx.map((entry) => (
                  <tr key={entry.id}>
                    <td className="px-4 py-2">{entry.date}</td>
                    <td className={`px-4 py-2 font-bold ${entry.type === 'income' ? 'text-blue-600' : 'text-red-600'}`}>{entry.type}</td>
                    <td className="px-4 py-2">{Number(entry.amount).toLocaleString()}</td>
                    <td className="px-4 py-2">{entry.description}</td>
                    <td className="px-4 py-2">{getUserById(entry.updatedBy)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-100 font-bold">
                  <td colSpan={2}>Total Income</td>
                  <td className="text-blue-700">{totalIncome.toLocaleString()}</td>
                  <td colSpan={2}></td>
                </tr>
                <tr className="bg-gray-100 font-bold">
                  <td colSpan={2}>Total Expense</td>
                  <td className="text-red-700">{totalExpense.toLocaleString()}</td>
                  <td colSpan={2}></td>
                </tr>
                <tr className="bg-gray-200 font-bold">
                  <td colSpan={2}>Net</td>
                  <td className={net >= 0 ? 'text-blue-700' : 'text-red-700'}>{net.toLocaleString()}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
            {/* Pagination Controls */}
            <div className="flex items-center justify-between mt-2">
              <button onClick={() => setTxPage(p => Math.max(0, p - 1))} disabled={txPage === 0} className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50">Previous</button>
              <span>Page {txPage + 1} of {Math.ceil(filteredCashbook.length / ROWS_PER_PAGE)}</span>
              <button onClick={() => setTxPage(p => p + 1)} disabled={(txPage + 1) * ROWS_PER_PAGE >= filteredCashbook.length} className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50">Next</button>
            </div>
          </div>
        </div>
      )}

      {/* Audit Log Tab */}
      {tab === 'audit' && (
        <div>
          <h3 className="text-xl font-semibold mb-2">Reversed Transactions (Audit Log)</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white shadow rounded">
              <thead>
                <tr className="bg-gray-200">
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">Action</th>
                  <th className="px-4 py-2">Details</th>
                  <th className="px-4 py-2">User</th>
                </tr>
              </thead>
              <tbody>
                {pagedAudit.map((entry) => (
                  <tr key={entry.id}>
                    <td className="px-4 py-2">{entry.date}</td>
                    <td className="px-4 py-2 text-red-600 font-bold">Reversal</td>
                    <td className="px-4 py-2">{entry.text}</td>
                    <td className="px-4 py-2">{getUserById(entry.uid)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Pagination Controls */}
            <div className="flex items-center justify-between mt-2">
              <button onClick={() => setAuditPage(p => Math.max(0, p - 1))} disabled={auditPage === 0} className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50">Previous</button>
              <span>Page {auditPage + 1} of {Math.ceil(filteredAudit.length / ROWS_PER_PAGE)}</span>
              <button onClick={() => setAuditPage(p => p + 1)} disabled={(auditPage + 1) * ROWS_PER_PAGE >= filteredAudit.length} className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50">Next</button>
            </div>
          </div>
        </div>
      )}

      {/* By User Tab */}
      {tab === 'users' && (
        <div>
          {!selectedUser ? (
            <div>
              <h3 className="text-xl font-semibold mb-2">Users</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white shadow rounded">
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="px-4 py-2">User</th>
                      <th className="px-4 py-2">Transactions</th>
                      <th className="px-4 py-2">Reversals</th>
                      <th className="px-4 py-2">Drill Down</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedUsers.map((user) => (
                      <tr key={user.id}>
                        <td className="px-4 py-2">{user.firstname}</td>
                        <td className="px-4 py-2">{user.transactionCount}</td>
                        <td className="px-4 py-2">{user.auditCount}</td>
                        <td className="px-4 py-2">
                          <button className="bg-blue-900 text-white px-3 py-1 rounded" onClick={() => setSelectedUser(user)}>
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Pagination Controls */}
                <div className="flex items-center justify-between mt-2">
                  <button onClick={() => setUserPage(p => Math.max(0, p - 1))} disabled={userPage === 0} className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50">Previous</button>
                  <span>Page {userPage + 1} of {Math.ceil(userActions.length / ROWS_PER_PAGE)}</span>
                  <button onClick={() => setUserPage(p => p + 1)} disabled={(userPage + 1) * ROWS_PER_PAGE >= userActions.length} className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50">Next</button>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <button className="mb-4 bg-gray-300 px-3 py-1 rounded" onClick={() => setSelectedUser(null)}>&larr; Back to Users</button>
              <h3 className="text-xl font-semibold mb-2">User: {selectedUser.firstname}</h3>
              <div className="mb-6">
                <h4 className="font-bold mb-2">Transactions</h4>
                <table className="min-w-full bg-white shadow rounded mb-4">
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="px-4 py-2">Date</th>
                      <th className="px-4 py-2">Type</th>
                      <th className="px-4 py-2">Amount</th>
                      <th className="px-4 py-2">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedUserTx.map((entry) => (
                      <tr key={entry.id}>
                        <td className="px-4 py-2">{entry.date}</td>
                        <td className={`px-4 py-2 font-bold ${entry.type === 'income' ? 'text-blue-600' : 'text-red-600'}`}>{entry.type}</td>
                        <td className="px-4 py-2">{Number(entry.amount).toLocaleString()}</td>
                        <td className="px-4 py-2">{entry.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Pagination Controls */}
                <div className="flex items-center justify-between mt-2">
                  <button onClick={() => setUserTxPage(p => Math.max(0, p - 1))} disabled={userTxPage === 0} className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50">Previous</button>
                  <span>Page {userTxPage + 1} of {Math.ceil(selectedUser.transactions.length / ROWS_PER_PAGE)}</span>
                  <button onClick={() => setUserTxPage(p => p + 1)} disabled={(userTxPage + 1) * ROWS_PER_PAGE >= selectedUser.transactions.length} className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50">Next</button>
                </div>
              </div>
              <div>
                <h4 className="font-bold mb-2">Reversals (Audit Log)</h4>
                <table className="min-w-full bg-white shadow rounded">
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="px-4 py-2">Date</th>
                      <th className="px-4 py-2">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedUserAudit.map((entry) => (
                      <tr key={entry.id}>
                        <td className="px-4 py-2">{entry.date}</td>
                        <td className="px-4 py-2">{entry.text}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Pagination Controls */}
                <div className="flex items-center justify-between mt-2">
                  <button onClick={() => setUserAuditPage(p => Math.max(0, p - 1))} disabled={userAuditPage === 0} className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50">Previous</button>
                  <span>Page {userAuditPage + 1} of {Math.ceil(selectedUser.audits.length / ROWS_PER_PAGE)}</span>
                  <button onClick={() => setUserAuditPage(p => p + 1)} disabled={(userAuditPage + 1) * ROWS_PER_PAGE >= selectedUser.audits.length} className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50">Next</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AuditPage;
